import { useCallback, useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Application,
  ReviewStatus,
  ProgressStatus,
  ProgressType,
  type Progress,
  OverallStatus,
  type ReviewerData,
  type ProgressEventError,
  type ProgressEventPhase,
} from "@/types/application";
import { ApplicationStatusTag } from "@/components/application-status-tag";
import { cn } from "@/utils/cn";
import { Check } from "lucide-react";
import {
  BranchIcon,
  LighteningIcon,
  SignIcon,
  WarningIcon,
  UserIcon,
} from "@/components/icons";
import BtnArrow from "@ui/arrow-button";
import {
  ReviewCard,
  UnassignedReviewCard,
} from "@/components/application-progress/review-card";

import { useApplicationProgress } from "@/hooks/useApplication";
import { Button } from "@/components/ui/button";

// --- Helper Functions -------------------------------------------------------

const getAllStepIds = (steps: Progress): string[] => {
  let ids: string[] = [];
  for (const step of steps) {
    ids.push(step.id);
    if (step.type === ProgressType.Condition) {
      for (const branch of step.children) {
        ids = ids.concat(getAllStepIds(branch));
      }
    }
  }
  return ids;
};

const findInProgressStep = (steps: Progress): Progress[number] | undefined => {
  for (const step of steps) {
    if (
      step.status === ProgressStatus.Pending ||
      step.status === ProgressStatus.NotStarted ||
      step.status === ProgressStatus.Tbd
    ) {
      return step;
    }
    if (step.type === ProgressType.Condition) {
      for (const branch of step.children) {
        const found = findInProgressStep(branch);
        if (found) return found;
      }
    }
  }
  return undefined;
};

const getIcon = (type: ProgressType, status: ProgressStatus) => {
  switch (status) {
    case ProgressStatus.Approved:
      return (
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-green">
          <Check className="text-white h-4 w-4" />
        </div>
      );
    case ProgressStatus.Pending:
      return (
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-giant-blue">
          {type === ProgressType.Condition ? (
            <BranchIcon className="h-5 w-5 text-white" />
          ) : (
            <SignIcon className="h-5 w-5 text-white" />
          )}
        </div>
      );
    case ProgressStatus.NotStarted:
    case ProgressStatus.Tbd:
      return (
        <div className="flex h-7 w-7 items-center justify-center rounded-full border border-stroke bg-white">
          {type === ProgressType.Condition ? (
            <BranchIcon className="h-5 w-5 text-primary-text" />
          ) : (
            <SignIcon className="h-5 w-5 text-primary-text" />
          )}
        </div>
      );
    case ProgressStatus.Rejected:
      return (
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-red">
          <WarningIcon className="text-white h-5 w-5" />
        </div>
      );
    default:
      return (
        <div className="flex h-7 w-7 items-center justify-center rounded-full border border-stroke bg-white">
          {type === ProgressType.Condition ? (
            <BranchIcon className="h-5 w-5 text-primary-text" />
          ) : (
            <SignIcon className="h-5 w-5 text-primary-text" />
          )}
        </div>
      );
  }
};

const getTimeLineColor = (status: ProgressStatus) => {
  switch (status) {
    case ProgressStatus.Approved:
      return "before:bg-green";
    case ProgressStatus.Pending:
      return "before:bg-giant-blue";
    case ProgressStatus.NotStarted:
    case ProgressStatus.Canceled:
    case ProgressStatus.Tbd:
      return "before:bg-gray-300";
    case ProgressStatus.Rejected:
      return "before:bg-red";
  }
};

const getTimelineBackgroundColor = (status: ProgressStatus) => {
  switch (status) {
    case ProgressStatus.Approved:
      return "bg-green";
    case ProgressStatus.Pending:
      return "bg-giant-blue";
    case ProgressStatus.NotStarted:
    case ProgressStatus.Canceled:
    case ProgressStatus.Tbd:
      return "bg-gray-300";
    case ProgressStatus.Rejected:
      return "bg-red";
  }
};

interface ProgressStepProps {
  step: Progress[number];
  index: number;
  isLast: boolean;
  expandedSteps: Set<string>;
  toggleStep: (stepId: string) => void;
  canRetryErroredApplication?: boolean;
  isRetryingErroredApplication?: boolean;
  onRetryErroredApplication?: () => void;
  retryWorkflowVersion?: string;
  scrollTo?: boolean;
  isConditionChild?: boolean;
}

const ProgressStep = ({
  step,
  index,
  expandedSteps,
  toggleStep,
  canRetryErroredApplication = false,
  isRetryingErroredApplication = false,
  onRetryErroredApplication,
  retryWorkflowVersion,
  scrollTo = false,
  isConditionChild = false,
}: ProgressStepProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const isExpanded = expandedSteps.has(step.id);
  const showToggle = step.children && step.children.length > 0;

  useEffect(() => {
    if (scrollTo) {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [scrollTo]);

  const renderRetryCard = () => {
    if (!canRetryErroredApplication || !onRetryErroredApplication) return null;

    return (
      <div className="ml-[38px] mt-2.5 overflow-hidden rounded-md border border-stroke bg-white">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium leading-[22px] text-dark">
              {retryWorkflowVersion
                ? t("application_page.retry_error_card_title", {
                    version: retryWorkflowVersion,
                  })
                : t("application_page.retry_error_card_title_fallback")}
            </div>
            <div className="text-xs font-medium leading-4 text-secondary-text">
              {retryWorkflowVersion
                ? t("application_page.retry_error_card_description", {
                    version: retryWorkflowVersion,
                  })
                : t("application_page.retry_error_card_description_fallback")}
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            className="shrink-0 bg-gray-700 px-3 py-[3px] text-sm font-medium leading-[22px] text-white hover:bg-gray-800"
            loading={isRetryingErroredApplication}
            onClick={onRetryErroredApplication}
          >
            {isRetryingErroredApplication
              ? t("application_page.retry_error_in_progress")
              : t("application_page.retry_error_action")}
          </Button>
        </div>
      </div>
    );
  };

  const renderErrorCard = () => (
    <>
      <div className="ml-[38px] mt-2 overflow-hidden rounded-md border border-red bg-white">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-2">
            <UserIcon className="h-6 w-6 text-secondary-text" />
          </div>
          <div className="min-w-0 flex-1 text-sm font-medium leading-6 text-red">
            Approver not found
          </div>
        </div>
      </div>
      {renderRetryCard()}
    </>
  );

  const getEventErrorTitle = (phase: ProgressEventPhase) =>
    phase === "taskEnter" || phase === "taskLeave"
      ? "Task Event execute failed"
      : "Node Event execute failed";

  const renderEventErrorCard = (event: ProgressEventError) => (
    <div key={event.id} className="relative flex gap-2">
      <div className="absolute left-[13px] -top-[9999px] bottom-[calc(100%_-_14px)] w-0.5 bg-red" />
      <div className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red">
        <LighteningIcon className="text-white" />
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="truncate text-sm font-medium leading-6 text-dark">
          {getEventErrorTitle(event.phase)}
        </div>
        <div className="break-words text-xs font-normal leading-4 text-primary-text">
          Please try again once the issue has been resolved by administrator.
        </div>
      </div>
    </div>
  );

  const renderEventErrorList = (
    events: ProgressEventError[] | undefined,
    className?: string,
  ) => {
    if (!events?.length) return null;
    return (
      <div className={cn("flex flex-col gap-2.5", className)}>
        {events.map(renderEventErrorCard)}
      </div>
    );
  };

  const getReviewerEventErrors = (
    events: ProgressEventError[] | undefined,
    phase: "taskEnter" | "taskLeave",
  ) => events?.filter((event) => event.phase === phase);

  const renderReviewerList = (reviewers: ReviewerData[]) => (
    <div className="mt-2.5 flex flex-col gap-2.5">
      {reviewers.map((node, nodeIndex) => {
        const taskEnterErrors = getReviewerEventErrors(
          node.eventErrors,
          "taskEnter",
        );
        const taskLeaveErrors = getReviewerEventErrors(
          node.eventErrors,
          "taskLeave",
        );

        return (
          <div
            key={node.id || `${node.assigneeName}-${nodeIndex}`}
            className="flex flex-col gap-2.5"
          >
            {renderEventErrorList(taskEnterErrors)}
            <div className="ml-[38px]">
              {node.status === ReviewStatus.Tbd ? (
                <UnassignedReviewCard assignedBy={node.title} />
              ) : (
                <ReviewCard
                  node={node}
                  className={
                    node.status === ReviewStatus.Canceled
                      ? "opacity-50"
                      : undefined
                  }
                />
              )}
            </div>
            {renderEventErrorList(taskLeaveErrors)}
          </div>
        );
      })}
    </div>
  );

  const renderEventErrors = (
    phase: ProgressEventPhase,
    className = "mt-2.5",
  ) => {
    const events = step.eventErrors?.filter((event) => event.phase === phase);
    return renderEventErrorList(events, className);
  };

  function renderContent() {
    if (step.errorMessage) {
      return renderErrorCard();
    }

    if (step.type === ProgressType.Review) {
      const totalCount = step.children.length;
      const reviewerContent =
        step.status === ProgressStatus.Tbd && totalCount === 0 ? (
          <div className="ml-[38px] mt-2.5">
            <UnassignedReviewCard />
          </div>
        ) : (
          renderReviewerList(step.children)
        );

      if (step.status === ProgressStatus.Tbd && totalCount === 0) {
        return reviewerContent;
      }
      return reviewerContent;
    } else if (step.type === ProgressType.Group) {
      const isGroupStatusCanceled = step.status === ProgressStatus.Canceled;

      return (
        <>
          <div className="mt-2.5 flex flex-col gap-2.5">
            {step.children.map((group, groupIndex) => (
              <div key={groupIndex} className="flex flex-col gap-2.5">
                <div className="ml-[38px] flex flex-col gap-2.5">
                  {group.title && step.children.length > 1 && (
                    <div className="text-xs font-medium leading-5 text-primary-text">
                      {group.title}
                    </div>
                  )}
                </div>
                {renderEventErrorList(group.eventErrors)}
                {group.data.map((node, nodeIndex) => {
                  const taskEnterErrors = getReviewerEventErrors(
                    node.eventErrors,
                    "taskEnter",
                  );
                  const taskLeaveErrors = getReviewerEventErrors(
                    node.eventErrors,
                    "taskLeave",
                  );

                  return (
                    <div
                      key={
                        node.id ||
                        `${node.assigneeName}-${groupIndex}-${nodeIndex}`
                      }
                      className="flex flex-col gap-2.5"
                    >
                      {renderEventErrorList(taskEnterErrors)}
                      <div className="ml-[38px]">
                        {node.status === ReviewStatus.Tbd ? (
                          <UnassignedReviewCard assignedBy={node.title} />
                        ) : (
                          <ReviewCard
                            node={node}
                            className={
                              node.status === ReviewStatus.Canceled &&
                              !isGroupStatusCanceled
                                ? "opacity-50"
                                : undefined
                            }
                          />
                        )}
                      </div>
                      {renderEventErrorList(taskLeaveErrors)}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      );
    } else if (step.type === ProgressType.Condition) {
      const branchSteps = step.children.flatMap((branch, branchIndex) =>
        branch.map((node, nodeIndex) => ({
          key: `${branchIndex}-${node.id}`,
          node,
          nodeIndex,
        })),
      );

      return (
        <div className="ml-[38px] mt-2.5">
          {branchSteps.map(({ key, node, nodeIndex }, flatIndex) => (
            <ProgressStep
              key={key}
              step={node}
              index={nodeIndex}
              isLast={flatIndex === branchSteps.length - 1}
              expandedSteps={expandedSteps}
              toggleStep={toggleStep}
              canRetryErroredApplication={canRetryErroredApplication}
              isRetryingErroredApplication={isRetryingErroredApplication}
              onRetryErroredApplication={onRetryErroredApplication}
              retryWorkflowVersion={retryWorkflowVersion}
              isConditionChild
            />
          ))}
        </div>
      );
    }
    return null;
  }

  const isCompletedStep = step.type === ProgressType.End;
  const title =
    isCompletedStep || isConditionChild
      ? step.title
      : `${index + 1}. ${step.title}`;

  return (
    <div
      ref={ref}
      className={cn(
        "step relative overflow-y-clip pb-5 before:h-full before:bg-gray-300",
        step.type === ProgressType.Condition && isExpanded
          ? "last:before:h-full"
          : "last:before:h-7",
        getTimeLineColor(step.status),
        step.status === ProgressStatus.Canceled && "opacity-50",
      )}
    >
      {isExpanded && renderEventErrors("nodeEnter", "mb-2.5")}
      {isConditionChild && (
        <div
          className={cn(
            "absolute top-[13px] -left-[25px] h-0.5 w-[25px]",
            getTimelineBackgroundColor(step.status),
          )}
        />
      )}
      {/* Step Title */}
      <div className="flex items-center gap-3">
        {/* Stepper icon */}
        <div className={"relative z-10 h-7 w-7"}>
          <div className="absolute inset-0 flex items-center justify-center">
            {getIcon(step.type, step.status)}
          </div>
        </div>
        {/* Collapse button */}
        <div className="flex flex-1 items-center">
          <div className="flex flex-1 text-base font-semibold leading-6 text-dark">
            {title}
          </div>
          {showToggle && (
            <BtnArrow
              direction={isExpanded ? "up" : "down"}
              onClick={() => toggleStep(step.id)}
            />
          )}
        </div>
      </div>
      {isExpanded && renderContent()}
      {isExpanded && renderEventErrors("nodeLeave", "mt-2.5")}
    </div>
  );
};

// --- Main Component ---------------------------------------------------------

interface ApplicationProgressProps {
  application: Application;
  canRetryErroredApplication?: boolean;
  isRetryingErroredApplication?: boolean;
  onRetryErroredApplication?: () => void;
}

export default function ApplicationProgress({
  application,
  canRetryErroredApplication = false,
  isRetryingErroredApplication = false,
  onRetryErroredApplication,
}: ApplicationProgressProps) {
  const { progress, isLoading } = useApplicationProgress(
    application.serialNumber,
  );
  const workflowVersion = useMemo(() => {
    const { major, minor } = application.workflowInstance.workflow;
    if (typeof major !== "number") return undefined;
    return typeof minor === "number" && minor > 0
      ? `v${major}.${minor}`
      : `v${major}`;
  }, [application.workflowInstance.workflow]);

  if (application.overallStatus === OverallStatus.Draft) {
    return (
      <div className="lg:max-w-[476px] w-full bg-gray-2 h-full flex items-center justify-center py-10 text-primary-text">
        Submit to see application progress
      </div>
    );
  }

  return (
    <>
      <div className="lg:max-w-[476px] w-full bg-gray-2 overflow-y-auto min-h-0 max-h-full">
        <div className="flex flex-col border-stroke">
          {/* <ProgressTabs /> */}
          {/* Header */}
          <div className="bg-white border-stroke flex min-h-[58px] items-center border-b px-5 sticky top-0 z-15">
            <span className="text-dark flex-1 text-base font-semibold">
              Overall progress
            </span>
            <ApplicationStatusTag status={progress?.overallStatus ?? null} />
          </div>

          {/* Progress flow */}
          {isLoading && "loading"}
          {!progress && "no progress"}
          {progress && (
            <Progress
              progress={progress.progress}
              overallStatus={progress.overallStatus}
              canRetryErroredApplication={canRetryErroredApplication}
              isRetryingErroredApplication={isRetryingErroredApplication}
              onRetryErroredApplication={onRetryErroredApplication}
              retryWorkflowVersion={workflowVersion}
            />
          )}
        </div>
      </div>
    </>
  );
}

type ProgressProps = {
  progress: Progress;
  overallStatus: OverallStatus;
  canRetryErroredApplication?: boolean;
  isRetryingErroredApplication?: boolean;
  onRetryErroredApplication?: () => void;
  retryWorkflowVersion?: string;
  //   expandedSteps: Set<string>;
  //   toggleStep: (stepId: string) => void;
  //   inProgressStep: Progress[number] | undefined;
};
export function Progress({
  progress,
  overallStatus,
  canRetryErroredApplication = false,
  isRetryingErroredApplication = false,
  onRetryErroredApplication,
  retryWorkflowVersion,
}: ProgressProps) {
  const allStepIds = useMemo(
    () => new Set(getAllStepIds(progress)),
    [progress],
  );
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(allStepIds);

  useEffect(() => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      let hasNewStep = false;
      allStepIds.forEach((stepId) => {
        if (!next.has(stepId)) {
          next.add(stepId);
          hasNewStep = true;
        }
      });
      return hasNewStep ? next : prev;
    });
  }, [allStepIds]);

  const toggleStep = useCallback((stepId: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  }, []);
  const inProgressStep = useMemo(
    () => findInProgressStep(progress),
    [progress],
  );
  return (
    <div
      className={cn(
        "p-5",
        overallStatus === OverallStatus.Canceled && "opacity-50",
      )}
    >
      <div className="relative">
        {progress.map((step, index) => (
          <ProgressStep
            key={step.id}
            step={step}
            index={index}
            isLast={index === progress.length - 1}
            expandedSteps={expandedSteps}
            toggleStep={toggleStep}
            canRetryErroredApplication={canRetryErroredApplication}
            isRetryingErroredApplication={isRetryingErroredApplication}
            onRetryErroredApplication={onRetryErroredApplication}
            retryWorkflowVersion={retryWorkflowVersion}
            scrollTo={step.id === inProgressStep?.id}
          />
        ))}
      </div>
    </div>
  );
}
